import type { ReactNode } from 'react';
import { useIxmUserauthV1TenantQueryServiceTenantGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-tenant-query-service-tenant-get';
import {
  getReadonlyListItemId,
  getReadonlyListItemJson,
  getReadonlyListItemNode,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
  type ReadonlyListItemOpts,
} from '@interxfi/app-components/components/readonly-list/util';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import type { ReadonlyListItem, ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import { DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type { IxmUserauthV1TenantSpec, IxmUserauthV1TenantState, IxmUserauthV1TenantType } from '@interxfi/types';
import { getBooleanFromUndefinedOrNullable } from '@interxfi/utilities/object/access';
import { getJ5StateMetadataListItems } from '../../j5/state/j5-state-list-items';
import { TenantLogomark, type TenantLogomarkProps } from '../../tenant/tenant-logomark';
import { getUserauthRealmDetailPath } from '../realm/userauth-realm-routing';
import { TenantStatusPill } from './userauth-tenant-status-pill';

export function getReadonlyListItemTenant(
  id: string,
  label: ReactNode,
  tenantType: string | undefined,
  tenantId: string | undefined,
  opts?: ReadonlyListItemOpts & Omit<TenantLogomarkProps, 'tenantId' | 'tenantType'>,
): ReadonlyListItem {
  const { nameTypography, overrideSrc, shouldLoadLogoMark = false, size = 'small', withName = true, ...restOfOpts } = opts ?? {};

  return {
    id,
    title: label,
    padding: DEFAULT_READONLY_LIST_ITEM_INTERACTIVE_PADDING,
    value: {
      node: {
        value: (
          <TenantLogomark
            nameTypography={nameTypography}
            overrideSrc={overrideSrc}
            shouldLoadLogoMark={shouldLoadLogoMark}
            size={size}
            tenantId={tenantId}
            tenantType={tenantType}
            withName={withName}
          />
        ),
      },
    },
    ...restOfOpts,
  };
}

export function getUserauthTenantTypeListItems(data: IxmUserauthV1TenantType | undefined, prefix = '') {
  return {
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    label: getReadonlyListItemString(`${prefix}label`, 'Label', data?.label),
    singular: getReadonlyListItemYesOrNo(`${prefix}singular`, 'Singular', getBooleanFromUndefinedOrNullable(data, 'singular')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getUserauthTenantSpecListItems(data: IxmUserauthV1TenantSpec | undefined, prefix = '') {
  return {
    name: getReadonlyListItemString(`${prefix}name`, 'Name', data?.name),
    metadata: getReadonlyListItemJson(`${prefix}metadata`, 'Metadata', data?.metadata),
  } as const satisfies ReadonlyListItemRecord;
}

export function TenantNameNode({ tenantId }: { tenantId: string | undefined }) {
  const { data, isPending } = useIxmUserauthV1TenantQueryServiceTenantGet(tenantId ? { tenantId } : undefined);
  if (isPending) {
    return <Skeleton charCount={12} />;
  }
  return data?.tenant?.data?.spec?.name || `Tenant ${getShortenedId(tenantId)}`;
}

export function getTenantNameListItem(id: string, label: string, tenantId: string | undefined) {
  return getReadonlyListItemNode(id, label, <TenantNameNode tenantId={tenantId} />);
}

export function getUserauthTenantStateListItems(data: IxmUserauthV1TenantState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    tenantId: getReadonlyListItemId('tenantId', 'Tenant ID', data?.tenantId),
    realmId: getReadonlyListItemId('realmId', 'Realm ID', data?.realmId, getUserauthRealmDetailPath(data?.realmId)),
    tenantType: getReadonlyListItemString('tenantType', 'Type', data?.tenantType),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixmUserauthV1',
      enumName: 'IxmUserauthV1TenantStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <TenantStatusPill status={data.status} /> : null),
    data: {
      spec: getUserauthTenantSpecListItems(data?.data?.spec, 'data.spec.'),
    },
  } as const satisfies ReadonlyListItemRecord;
}
